import React, { useEffect, useRef, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { EASE_CUBIC } from '@pkg/components/Sections/Profile/StyledThemeProvider/themes/default';
import { useBreakpoints } from '@pkg/hooks';

import { useGetWishlistMetadata } from '@app/pages/Wishlist/hooks/dataSources/useGetWishlistMetadata';

import {
    ActiveLine,
    ButtonWrapper,
    HeaderNavButton,
    Separator,
    StatHeading,
    StatItem,
    StatLabel,
    StatsWrapper,
    Wrapper,
} from './styles';

export enum WishlistTabs {
    Collections = 'Collections',
    Collectibles = 'Collectibles',
}
interface NavigationBarProps {
    tabs: WishlistTabs[];
}

export const NavigationBar = ({ tabs }: NavigationBarProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { isMobile } = useBreakpoints();

    const { data: metaData, isLoading } = useGetWishlistMetadata();

    const [activeTab, setActiveTab] = useState<WishlistTabs | null>(WishlistTabs.Collections);

    const [activeLine, setActiveLine] = useState<{ width: number; x: number }>(
        isMobile ? { width: 98, x: 20 } : { width: 89, x: 0 },
    );

    const [searchParams] = useSearchParams();

    useEffect(() => {
        const currentTab = searchParams.get('tab') as WishlistTabs;
        if (currentTab) {
            setActiveTab(currentTab);
        }
    }, [searchParams]);

    useEffect(() => {
        if (!wrapperRef.current) return;

        const target = wrapperRef.current.querySelector(`[data-active="true"]`) as HTMLElement;
        if (!target) return;

        const timer = setTimeout(() => {
            animateLine(target);
        }, 500);

        return () => {
            clearTimeout(timer);
        };
    }, [activeTab]);

    const navigate = useNavigate();

    const animateLine = (target: HTMLElement) => {
        if (!wrapperRef.current) return;
        const position = target.getBoundingClientRect();
        const parentPosition = wrapperRef.current.getBoundingClientRect();

        setActiveLine({
            width: position.width,
            x: position.x - parentPosition.x,
        });
    };

    const handleClick = (value: WishlistTabs, target: HTMLElement) => {
        navigate(`?tab=${value}`);
        animateLine(target);
        setActiveTab(value);
    };

    useEffect(() => {
        if (!wrapperRef.current) return;

        const node = wrapperRef.current.querySelector(`[data-active="true"]`) as HTMLElement;
        if (!node) return;

        const timer = setTimeout(() => {
            if (!wrapperRef.current) return;
            const position = node.getBoundingClientRect();
            const parentPosition = wrapperRef.current.getBoundingClientRect();
            setActiveLine({
                width: position.width,
                x: position.x - parentPosition.x,
            });
        }, 500);

        return () => {
            clearTimeout(timer);
        };
    }, []);

    return (
        <Wrapper>
            <ButtonWrapper ref={wrapperRef}>
                {tabs.map((type) => (
                    <HeaderNavButton
                        key={`tab-${type}`}
                        data-active={activeTab === type}
                        $isActive={activeTab === type}
                        onClick={(e) => {
                            handleClick(type, e.target as HTMLElement);
                        }}
                    >
                        {type}
                    </HeaderNavButton>
                ))}

                <ActiveLine animate={activeLine} transition={{ duration: 0.6, ease: EASE_CUBIC }} />
            </ButtonWrapper>

            {!isMobile && !isLoading && metaData?.counts && (
                <StatsWrapper>
                    <StatItem>
                        <StatLabel>Saved items</StatLabel>
                        <StatHeading>{metaData?.counts.total}</StatHeading>
                    </StatItem>

                    <Separator />

                    <StatItem>
                        <StatLabel>Collectibles</StatLabel>
                        <StatHeading>{metaData?.counts.token_count}</StatHeading>
                    </StatItem>
                    <StatItem>
                        <StatLabel>Collections</StatLabel>
                        <StatHeading>{metaData?.counts.collection_count}</StatHeading>
                    </StatItem>
                    {/*}
                        <StatItem>
                            <StatLabel>Traits</StatLabel>
                            <StatHeading>{metaData?.counts.trait_count}</StatHeading>
                        </StatItem>
                        {*/}
                </StatsWrapper>
            )}
        </Wrapper>
    );
};
